<template>
  <div>
    <ElForm
      ref="phoneNumber"
      :model="model"
      :rules="rules"
      label-position="top"
      @submit.native.prevent="checkPhoneAndSendSms"
    >
      <ElFormItem :label="$t('PHONE')" prop="phoneNumber">
        <ElInput v-model="model.phoneNumber" name="phoneNumber" />
      </ElFormItem>

      <p v-if="errorMessage" class="text-alert text-small">
        {{ errorMessage }}
      </p>

      <ElButton
        type="primary"
        native-type="submit"
        size="large"
        :loading="sendingSms"
      >
        {{ $t('AUTH.STEPS.IDENTITY_VERIFICATION.SEND_CODE') }}
      </ElButton>

      <ElButton type="text" @click.native="$emit('cancel-edit')">
        {{ $t('CANCEL') }}
      </ElButton>
    </ElForm>

    <UpdatePhoneNumberModal
      v-model="updatePhoneNumberModal"
      :phone-number="model.phoneNumber"
      :wait-time="waitTime"
      :sending-sms="sendingSms"
      @send-sms="checkPhoneAndSendSms"
      @verify="afterVerifyPhone"
    />
  </div>
</template>

<script>
import { PhoneNumber } from '@/api/api';
import UpdatePhoneNumberModal from './UpdatePhoneNumberModal';

import required from '@/utils/validation-rules/required';
import form from '@/mixins/form';
import modal from '@/mixins/modal';
import { IRANIAN_MOBILE_PATTERN } from '@/utils/validations';

export default {
  name: 'PhoneNumberForm',

  components: {
    UpdatePhoneNumberModal,
  },

  mixins: [form, modal],

  props: {
    phone: {
      type: String,
      required: true,
    },
  },

  data() {
    return {
      model: {
        phoneNumber: this.phone,
      },
      updatePhoneNumberModal: false,
      errorMessage: null,
      waitTime: 0,
      sendingSms: false,
    };
  },

  computed: {
    hasEditPermission() {
      return this.$auth.check(['publisher', 'changeFinanceInfo']);
    },

    rules() {
      return {
        phoneNumber: [
          required(),
          {
            pattern: IRANIAN_MOBILE_PATTERN,
            trigger: 'blur',
            message: this.$t('PUBLISHER_ACCOUNT.INVALID_PHONE'),
          },
        ],
      };
    },
  },

  watch: {
    'model.phoneNumber': function() {
      this.resetTimer();
    },
  },

  methods: {
    checkPhoneAndSendSms() {
      if (!this.hasEditPermission || !this.isValid('phoneNumber')) {
        return false;
      }

      if (this.timer) {
        this.openModal('updatePhoneNumberModal');
        return false;
      }

      this.errorMessage = null;
      this.sendingSms = true;
      return PhoneNumber.sendSMS({ telephone_number: this.model.phoneNumber })
        .then(({ body: { time_to_wait } }) => {
          this.createTimer(time_to_wait);
          this.openModal('updatePhoneNumberModal');
        })
        .catch(({ body: { message } }) => {
          if (message) {
            this.$message.error(message);
          }

          this.errorMessage = message;
        })
        .finally(() => {
          this.sendingSms = false;
        });
    },

    showPhoneNumberError(errorMessage) {
      this.errorMessage = errorMessage;
      this.closeModal('updatePhoneNumberModal');
    },

    afterVerifyPhone() {
      this.closeModal('updatePhoneNumberModal');
      this.resetTimer();
      this.$emit('data-updated', this.model.phoneNumber);
    },

    createTimer(retryTime) {
      if (!this.timer) {
        this.waitTime = retryTime;
        this.timer = setInterval(this.calcCounter, 1000);
      }
    },

    calcCounter() {
      if (this.waitTime > 0) {
        this.waitTime -= 1;
      } else {
        clearInterval(this.timer);
        this.timer = null;
      }
    },

    resetTimer() {
      clearInterval(this.timer);
      this.errorMessage = null;
      this.timer = null;
      this.waitTime = 0;
    },
  },
};
</script>
